import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdClose, MdCloudUpload, MdHourglassTop, MdCheckCircle } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { getPlatformPaymentInfo, submitPlatformPayment } from '../services/subscriptionService';
import { formatAd, formatBs } from '../utils/calendar';
import DualDate from './common/DualDate';
import { SUPPORT_EMAIL } from '../config/support';

// Fallback owner details if the API somehow returns none — the backend already
// defaults the bank to these, so clubs always have a destination + help contact.
const OWNER_BANK     = { bank: 'Siddhartha Bank', name: 'Anush Dhakal', account: '00818287510' };
const OWNER_WHATSAPP = 'https://wa.me/61414934174';
const OWNER_EMAIL    = SUPPORT_EMAIL;

const METHODS = [
  { key: 'bank',    label: 'Bank Transfer' },
  { key: 'esewa',   label: 'eSewa' },
  { key: 'khalti',  label: 'Khalti' },
  { key: 'fonepay', label: 'Fonepay QR' },
  { key: 'cash',    label: 'Cash / Manual' },
];

const MAX_BYTES = 5 * 1024 * 1024;
const OK_TYPES  = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];

// Prepay discount ladder — mirrors backend/config/plans.js for DISPLAY only.
// The server recomputes the authoritative total on submit.
const prepayDiscountPercent = (months) => {
  const m = Number(months);
  if (!Number.isInteger(m) || m < 3) return 0;
  if (m < 6)  return 5;
  if (m < 12) return 10;
  return 15;
};
const MONTH_QUICK = [1, 3, 6, 12];
const npr = (n) => `NPR ${Number(n || 0).toLocaleString()}`;

// Add `n` CALENDAR months for the "covered until" preview — mirrors the backend
// addMonths (setMonth-based, clamps to month end). DISPLAY ESTIMATE only.
const addMonthsDisplay = (date, n) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + Number(n));
  if (d.getDate() < day) d.setDate(0);
  return d;
};

// Whether a given method has real configured details (so we only ever offer
// methods the club can actually pay to — never blank fields).
const methodHasDetails = (d, key) => {
  if (!d) return false;
  switch (key) {
    case 'esewa':   return !!d.esewaId;
    case 'khalti':  return !!d.khaltiId;
    case 'fonepay': return !!d.fonepayQrUrl;
    case 'bank':    return !!(d.bankName && d.bankAccountName && d.bankAccountNumber);
    case 'cash':    return true; // manual — always allowed
    default:        return false;
  }
};

const DetailRow = ({ k, v }) => v ? (
  <div className="flex justify-between gap-3 text-sm py-0.5">
    <span className="text-muted">{k}</span><span className="font-medium text-secondary text-right break-all">{v}</span>
  </div>
) : null;

// Gateway-specific destination (only for the non-bank methods).
function GatewayDetails({ method, details }) {
  if (!details) return null;
  if (method === 'esewa')   return <div className="rounded-xl bg-nav-hover/40 border border-divider p-3 mt-2"><DetailRow k="eSewa ID / mobile" v={details.esewaId} /></div>;
  if (method === 'khalti')  return <div className="rounded-xl bg-nav-hover/40 border border-divider p-3 mt-2"><DetailRow k="Khalti ID / mobile" v={details.khaltiId} /></div>;
  if (method === 'fonepay') return details.fonepayQrUrl
    ? <div className="rounded-xl bg-nav-hover/40 border border-divider p-3 mt-2"><img src={details.fonepayQrUrl} alt="Fonepay QR" className="mx-auto max-h-44 rounded-lg" /></div>
    : null;
  if (method === 'cash')    return <div className="rounded-xl bg-nav-hover/40 border border-divider p-3 mt-2"><p className="text-sm text-muted">Record a manual/cash payment and add the reference your TrainAxis contact gave you.</p></div>;
  return null;
}

export default function PlatformPaymentModal({ onClose, onSubmitted }) {
  const [method, setMethod]       = useState('bank');
  const [reference, setReference] = useState('');
  const [notes, setNotes]         = useState('');
  const [file, setFile]           = useState(null);
  const [months, setMonths]       = useState(1);
  const [submittedView, setSubmittedView] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['platform-payment-info'],
    queryFn:  () => getPlatformPaymentInfo().then(r => r.data),
    retry:    false,
  });

  const sub = data?.subscription;
  const pending = data?.pendingPayment;
  const pd = data?.paymentDetails || {};
  const isSuspended = sub?.status === 'suspended';
  // Subscription Ends and Next Payment Due are almost always the same date in practice
  // (nextPaymentDue is kept mirrored to subscriptionEndDate) — show it once, not twice under
  // different labels, unless they genuinely differ.
  const sameEndDate = !!sub?.subscriptionEndsAt && !!sub?.nextRenewalDate &&
    new Date(sub.subscriptionEndsAt).getTime() === new Date(sub.nextRenewalDate).getTime();

  // Real bank destination (backend defaults to the owner bank; env overrides).
  const bank = {
    bank:    pd.bankName || OWNER_BANK.bank,
    name:    pd.bankAccountName || OWNER_BANK.name,
    account: pd.bankAccountNumber || OWNER_BANK.account,
  };

  // Bank + cash are always available; gateways appear only when their env details exist.
  const availableMethods = METHODS.filter(m => methodHasDetails(pd, m.key));
  const methods = availableMethods.length ? availableMethods : METHODS.filter(m => m.key === 'bank' || m.key === 'cash');
  const methodKeys = methods.map(m => m.key).join(',');

  // Keep the selected method valid for whatever is available.
  useEffect(() => {
    const keys = methodKeys ? methodKeys.split(',') : [];
    if (keys.length && !keys.includes(method)) setMethod(keys[0]);
  }, [methodKeys, method]);

  const mutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('method', method);
      fd.append('months', String(months));
      if (reference) fd.append('referenceId', reference);
      if (notes)     fd.append('notes', notes);
      if (file)      fd.append('proof', file);
      return submitPlatformPayment(fd);
    },
    onSuccess: () => {
      toast.success('Payment submitted. Waiting for verification.');
      setSubmittedView(true);
      onSubmitted?.();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to submit payment'),
  });

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!OK_TYPES.includes(f.type)) { toast.error('Upload a JPG, PNG, WEBP or PDF file.'); return; }
    if (f.size > MAX_BYTES)         { toast.error('File must be 5 MB or smaller.'); return; }
    setFile(f);
  };

  const handleSubmit = () => {
    if (!file) {
      toast.error('Please upload payment proof before submitting.');
      return;
    }
    mutation.mutate();
  };

  // Already-submitted / waiting state (also blocks duplicate submissions)
  const waiting = submittedView || pending;

  // Live prepay math (DISPLAY only — the server recomputes the authoritative total).
  const perMonth    = Number(data?.amountDue || 0);
  const discountPct = prepayDiscountPercent(months);
  const gross       = perMonth * months;
  const total       = Math.round(gross * (1 - discountPct / 100));
  const save        = gross - total;

  // Live "covered until" preview — mirrors the backend rule: stack onto the later of
  // (today) and (current end if it's still in the future), then add the chosen months.
  // DISPLAY ESTIMATE only; the server computes the real end date on verify.
  const nowD       = new Date();
  const currentEnd = sub?.subscriptionEndsAt ? new Date(sub.subscriptionEndsAt) : null;
  const base       = (currentEnd && currentEnd > nowD) ? currentEnd : nowD;
  const coveredUntil = addMonthsDisplay(base, months);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-card border border-divider rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider sticky top-0 bg-card">
          <h2 className="text-lg font-bold text-secondary">Submit Payment</h2>
          <button onClick={onClose} className="p-1 rounded-lg text-muted hover:bg-nav-hover" aria-label="Close"><MdClose className="text-xl" /></button>
        </div>

        <div className="p-6">
          {isPending ? (
            <div className="h-40 rounded-xl bg-nav-hover animate-pulse" />
          ) : waiting ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mx-auto mb-4">
                <MdHourglassTop className="text-3xl text-amber-400" />
              </div>
              <h3 className="font-bold text-secondary">Payment submitted</h3>
              <p className="text-sm text-muted mt-2 max-w-xs mx-auto">
                Waiting for TrainAxis verification. You’ll be notified once it’s reviewed.
              </p>
              {(pending?.rejectionReason) && (
                <p className="text-xs text-red-400 mt-3">Previous note: {pending.rejectionReason}</p>
              )}
              <button onClick={onClose} className="mt-6 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark">Done</button>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="rounded-xl bg-nav-hover/40 border border-divider p-4 mb-4 space-y-1.5">
                <div className="flex justify-between text-sm"><span className="text-muted">Plan</span><span className="font-semibold text-secondary">{sub?.planName || 'Pro'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted">Per month</span><span className="font-semibold text-secondary">{npr(perMonth)}</span></div>
                {isSuspended ? (
                  // Lapsed/suspended: one line, matching the status card above this modal —
                  // "Subscription Ends" and "Next Payment Due" would otherwise both show the
                  // same already-passed date under labels that read like two separate debts.
                  <div className="flex justify-between text-sm"><span className="text-muted">Access stopped</span><span className="text-secondary text-right"><DualDate date={sub?.subscriptionEndsAt} /></span></div>
                ) : sameEndDate ? (
                  <div className="flex justify-between text-sm"><span className="text-muted">Subscription Ends</span><span className="text-secondary text-right"><DualDate date={sub?.subscriptionEndsAt} /></span></div>
                ) : (
                  <>
                    <div className="flex justify-between text-sm"><span className="text-muted">Subscription Ends</span><span className="text-secondary text-right"><DualDate date={sub?.subscriptionEndsAt} /></span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted">Next Payment Due</span><span className="text-secondary text-right"><DualDate date={sub?.nextRenewalDate} /></span></div>
                  </>
                )}
                {sub?.isOverdue && (
                  <div className="flex justify-between text-sm"><span className="text-muted">Overdue</span><span className="text-red-400 font-medium">{sub.overdueDays} day{sub.overdueDays === 1 ? '' : 's'}</span></div>
                )}
                {sub?.isOverdue && sub?.graceDaysRemaining != null && (
                  <div className="flex justify-between text-sm"><span className="text-muted">Grace Remaining</span><span className="text-red-400 font-medium">{sub.graceDaysRemaining > 0 ? `${sub.graceDaysRemaining} day${sub.graceDaysRemaining === 1 ? '' : 's'}` : 'Suspension imminent'}</span></div>
                )}
              </div>

              {/* Prepay & save — choose months; server confirms the total */}
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold text-muted uppercase tracking-wide">Prepay &amp; save</p>
                {discountPct >= 15 && (
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full">Save 15% — best value</span>
                )}
              </div>
              <div className="rounded-xl bg-nav-hover/40 border border-divider p-4 mb-4">
                {/* Quick picks */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {MONTH_QUICK.map(m => {
                    const active = months === m;
                    const pct = prepayDiscountPercent(m);
                    return (
                      <button key={m} type="button" onClick={() => setMonths(m)}
                        className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${active ? 'border-primary bg-nav-hover text-primary' : 'border-divider text-muted hover:bg-nav-hover'}`}>
                        <span className="block">{m} mo</span>
                        {pct > 0 && <span className="block text-[10px] font-bold text-emerald-400">-{pct}%</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Stepper — any 1–12 */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted">Months</span>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setMonths(m => Math.max(1, m - 1))} disabled={months <= 1}
                      className="w-8 h-8 rounded-lg border border-divider text-secondary hover:bg-nav-hover disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center">−</button>
                    <span className="w-8 text-center text-sm font-bold text-secondary">{months}</span>
                    <button type="button" onClick={() => setMonths(m => Math.min(12, m + 1))} disabled={months >= 12}
                      className="w-8 h-8 rounded-lg border border-divider text-secondary hover:bg-nav-hover disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center">+</button>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="space-y-1 pt-3 border-t border-divider">
                  <div className="flex justify-between text-sm"><span className="text-muted">{npr(perMonth)} × {months} mo</span><span className="text-secondary">{npr(gross)}</span></div>
                  {discountPct > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-emerald-400">You save ({discountPct}%)</span><span className="text-emerald-400">− {npr(save)}</span></div>
                  )}
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-sm font-semibold text-secondary">Total</span>
                    <span className="text-lg font-bold text-secondary">{npr(total)}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 pt-2 mt-1 border-t border-divider">
                    <span className="text-xs text-muted flex-shrink-0">Covered until</span>
                    <span className="text-xs font-medium text-secondary text-right">
                      {formatBs(coveredUntil)} <span className="text-muted font-normal">· {formatAd(coveredUntil)}</span>
                    </span>
                  </div>
                </div>

                {/* The "days left, no time lost" reassurance now lives on the status card
                    above this modal — repeating it here would duplicate, not inform. */}

                <p className="text-[11px] text-subtle mt-2">Total is calculated automatically and confirmed by TrainAxis on verification.</p>
              </div>

              {/* Pay-to bank details — always shown */}
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Pay to this account</p>
              <div className="rounded-xl bg-nav-hover/40 border border-divider p-3 mb-4 space-y-0.5">
                <DetailRow k="Bank" v={bank.bank} />
                <DetailRow k="Account Name" v={bank.name} />
                <DetailRow k="Account Number" v={bank.account} />
              </div>

              {/* Method */}
              <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Payment Method</label>
              <div className="grid grid-cols-3 gap-2">
                {methods.map(m => (
                  <button key={m.key} type="button" onClick={() => setMethod(m.key)}
                    className={`py-2 rounded-xl text-xs font-semibold border transition-colors ${method === m.key ? 'border-primary bg-nav-hover text-primary' : 'border-divider text-muted hover:bg-nav-hover'}`}>
                    {m.label}
                  </button>
                ))}
              </div>

              {/* Gateway-specific destination (bank already shown above) */}
              <GatewayDetails method={method} details={pd} />

              {/* Reference */}
              <label className="block text-xs font-semibold text-muted uppercase tracking-wide mt-4 mb-1.5">Transaction / Reference ID</label>
              <input value={reference} onChange={e => setReference(e.target.value)}
                placeholder="e.g. bank ref, eSewa txn ID"
                className="w-full bg-card border border-divider rounded-xl px-3 py-2.5 text-sm text-secondary placeholder:text-subtle outline-none focus:border-primary" />

              {/* Proof */}
              <label className="block text-xs font-semibold text-muted uppercase tracking-wide mt-4 mb-1.5">Payment Screenshot / Proof <span className="text-red-400">*</span></label>
              <label className="flex items-center gap-2 border border-dashed border-divider rounded-xl px-3 py-3 text-sm text-muted cursor-pointer hover:bg-nav-hover">
                <MdCloudUpload className="text-lg" />
                {file ? <span className="text-secondary truncate">{file.name}</span> : 'Upload JPG, PNG, WEBP or PDF (max 5 MB)'}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onPickFile} />
              </label>

              {/* Notes */}
              <label className="block text-xs font-semibold text-muted uppercase tracking-wide mt-4 mb-1.5">Notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="Anything the TrainAxis team should know"
                className="w-full bg-card border border-divider rounded-xl px-3 py-2.5 text-sm text-secondary placeholder:text-subtle outline-none focus:border-primary resize-none" />

              <button onClick={handleSubmit} disabled={mutation.isPending || !file}
                className="w-full mt-5 py-3 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <MdCheckCircle className="text-base" />
                {mutation.isPending ? 'Submitting…' : 'Submit Payment Proof'}
              </button>
              {!file && (
                <p className="text-xs text-amber-400 text-center mt-2">Please upload payment proof before submitting.</p>
              )}
              <p className="text-xs text-subtle text-center mt-2">Pay to the account above, then submit your proof here for verification.</p>

              {/* Help contact — demoted, not a payment method */}
              <div className="mt-4 pt-3 border-t border-divider text-center">
                <p className="text-xs text-subtle">
                  Need help? Contact us on{' '}
                  <a href={OWNER_WHATSAPP} target="_blank" rel="noreferrer" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
                    <FaWhatsapp className="text-xs" /> WhatsApp
                  </a>{' '}
                  or{' '}
                  <a href={`mailto:${OWNER_EMAIL}`} className="text-primary font-medium hover:underline">email</a>.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
